import { zodResolver } from '@hookform/resolvers/zod';
import { assert } from 'assert-ts';
import uniq from 'lodash/fp/uniq';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useAddTag, useEditTag, useSuspenseMaxTagsQuery } from '#api/tags.queries.ts';
import { AlertIcon } from '#assets/ReactSvg.ts';
import { AddButton } from '#components/atoms/buttons/add-button/AddButton.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, HintText, Input, Label } from '#components/atoms/forms/index.ts';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { NO_HOST_FLAG } from '#components/patterns/entity-select/host/SelectHost.queries.ts';
import { SelectHost } from '#components/patterns/entity-select/host/SelectHost.tsx';
import { CancelLinkButton } from '#components/patterns/forms/cancel-link-button/CancelLinkButton.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { FieldSet } from '#components/patterns/forms/fieldset/FieldSet.tsx';
import { ComboboxTags } from '#components/patterns/forms/select/combobox/combobox-tags/ComboboxTags.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { Route } from '#dn-types/route.ts';
import { tagNameSchema, type TagName } from '#schemas/tag/name.ts';
import { BlockNavigationDialog } from '#screens/roles/detail/form/BlockNavigationDialog.tsx';
import { SPR } from '#util/validation/zod-utils.ts';
import { AddCIDRFirewallRuleModal } from './firewall-rule/add/AddCIDRFirewallRuleModal.tsx';
import {
  cidrFirewallRuleFormSchema,
  type CIDRFirewallRuleFormData,
} from './firewall-rule/cidr-firewall-rule-form-schema.ts';
import { CIDRFirewallRulesList } from './firewall-rule/CIDRFirewallRulesList.tsx';
import { EditCIDRFirewallRuleModal } from './firewall-rule/edit/EditCIDRFirewallRuleModal.tsx';
import { mapFormToRoute } from './mapFormToRoute.ts';
import { AddCIDRModal } from './routable-cidr/AddCIDRModal.tsx';
import { EditCIDRModal } from './routable-cidr/EditCIDRModal.tsx';
import { RoutableCIDRs } from './routable-cidr/RoutableCIDRs.tsx';
import { routeFormSchema, type RouteFormData } from './route-form-schema.ts';
import { useAddRoute, useEditRoute } from './RouteForm.queries.ts';
import styles from './RouteForm.module.css';

type EditProps = {
  routeId: `route-${string}`;
  defaultValues: RouteFormData;
  onSuccess: () => void;
};

type AddProps = {
  routeId: null;
  onSuccess: () => void;
};

type Props = EditProps | AddProps;

const DEFAULT_VALUES: RouteFormData = {
  name: '',
  description: '',
  routerHostID: NO_HOST_FLAG,
  routableCIDRs: [],
  firewallRules: [],
};

const NO_ROUTER_OPTION = {
  value: NO_HOST_FLAG,
  label: 'No router',
  hint: 'Route will not function without a router',
} as const;

export function RouteForm(props: Props) {
  // This is the list of tag names which subscribed to the route when the page was opened.
  // We can't use react state for this, because it needs to be set synchronously before the form renders,
  // to use it as a defaultValue
  let startingTags: TagName[] = [];
  const [showAddCIDRModal, setShowAddCIDRModal] = useState(false);
  const [editCIDRIndex, setEditCIDRIndex] = useState<number | null>(null);
  const defaultValues = props.routeId ? props.defaultValues : DEFAULT_VALUES;

  // null = closed; defaultValues prefill the add modal when duplicating an existing rule
  const [addRuleModal, setAddRuleModal] = useState<{ defaultValues?: CIDRFirewallRuleFormData } | null>(null);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [blockNavigation, setBlockNavigation] = useState(false);

  const addRoute = useAddRoute();
  const editRoute = useEditRoute();
  const editTag = useEditTag();
  const addTag = useAddTag();
  const allTags = useSuspenseMaxTagsQuery().data.data;

  // Figure out what tags had this route to start with
  if (props.routeId) {
    startingTags = allTags.filter((tag) => tag.routeSubscriptions.includes(props.routeId)).map((tag) => tag.name);
  }

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { isDirty: isRouteDirty, errors, isSubmitted, isValid },
  } = useForm({
    resolver: zodResolver(routeFormSchema),
    defaultValues,
  });
  const routerHostID = useWatch({ control, name: 'routerHostID' });

  const {
    control: tagsControl,
    formState: tagsFormState,
    getValues: getTagsValues,
  } = useForm({
    defaultValues: { tags: startingTags },
    resolver: zodResolver(z.object({ tags: tagNameSchema.array() })),
  });

  const {
    fields: cidrs,
    remove: removeCIDR,
    append: appendCIDR,
    update: updateCIDR,
  } = useFieldArray({
    control,
    name: 'routableCIDRs',
  });

  const {
    fields: firewallRuleFieldsUnparsed,
    remove: removeRule,
    append: appendRule,
    update: updateRule,
  } = useFieldArray({
    control,
    name: 'firewallRules',
  });

  // The parse restores the branded types that useFieldArray widens (e.g. tag names) and strips
  // react-hook-form's generated field id, so entries are safe to reuse as rule values (e.g. as
  // modal prefills when duplicating)
  const firewallRuleFields = z.array(cidrFirewallRuleFormSchema).parse(firewallRuleFieldsUnparsed);

  const valueBeingEdited = editCIDRIndex !== null && cidrs[editCIDRIndex];
  // Need to check both forms' states
  const isDirty = isRouteDirty || tagsFormState.isDirty;

  // Prompt before navigation if form is dirty
  // Note: this is disabled during submission
  useEffect(() => {
    setBlockNavigation(isDirty);
  }, [isDirty]);

  const onSubmit = handleSubmit(async (data) => {
    setBlockNavigation(false);
    let newRoute: Route | undefined;

    // Treat errors from route creating/edit differently from those when adding/editing tags
    try {
      newRoute =
        props.routeId ?
          await editRoute.mutateAsync({ routeId: props.routeId, variables: mapFormToRoute(data) })
        : await addRoute.mutateAsync(mapFormToRoute(data));
    } catch (routeErr) {
      if (routeErr instanceof ValidationError) {
        handleServerFormError(routeErr, ({ path, message }) => {
          switch (path) {
            case 'name':
            case 'description':
            case 'routerHostID':
            case 'routableCIDRs':
            case 'firewallRules':
              setError(path, { message }, { shouldFocus: true });
              return true;
            case 'root.global':
            case undefined:
              setError('root.global', { message });
              return true;
            default:
              setError('root.global', { message });
              return false;
          }
        });
      } else {
        trackError(routeErr);
        setError('root.global', {
          message: `We couldn’t ${props.routeId ? 'edit' : 'add'} the route, please try again`,
        });
      }
    }

    if (newRoute) {
      try {
        // Get the tag names from the tag combobox
        const tagNames = getTagsValues().tags;
        const validTagNames = tagNames.map((t) => SPR(tagNameSchema.safeParse(t)).data).filter(Boolean);

        // Loop through each tag name and subscribe the route to the tag, potentially creating the tag as well
        await Promise.all(
          validTagNames.map(async (tagName) => {
            const tag = allTags.find((t) => t.name === tagName);

            if (tag) {
              await editTag.mutateAsync({
                name: tag.name,
                variables: { routeSubscriptions: uniq([...tag.routeSubscriptions, newRoute.id]) },
              });
            } else {
              await addTag.mutateAsync({
                name: tagName,
                description: '',
                configOverrides: [],
                routeSubscriptions: [newRoute.id],
              });
            }
          })
        );

        await Promise.all(
          // Unassign any tags that were removed from the page
          startingTags.map(async (startingTag) => {
            if (!tagNames.includes(startingTag)) {
              const tag = allTags.find((t) => t.name === startingTag);
              if (tag) {
                await editTag.mutateAsync({
                  name: startingTag,
                  variables: { routeSubscriptions: tag.routeSubscriptions.filter((rs) => rs !== newRoute.id) },
                });
              }
            }
          })
        );
      } catch (tagErr) {
        // Track tag errors, but don't stop the form from submitting
        trackError(tagErr);
      } finally {
        props.onSuccess();
      }
    }
  });

  return (
    <>
      <BlockNavigationDialog when={blockNavigation} title="Unsaved changes">
        You have unsaved changes, are you sure you want to leave?
      </BlockNavigationDialog>
      <form action="post" id="route-form" onSubmit={onSubmit}>
        <Spacer direction="vertical" size="xxwide">
          <FieldSet legend="Define route">
            {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}

            <Field label="Name">
              <Input className="width-100" {...register('name')} hasError={Boolean(errors.name)} />
              {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
            </Field>
            <Field label="Description">
              <Input className="width-100" {...register('description')} hasError={Boolean(errors.description)} />
              {errors.description && <ErrorMessage>{errors.description.message}</ErrorMessage>}
            </Field>
            <div>
              <SelectHost
                label="Router"
                name="routerHostID"
                className="width-100"
                control={control}
                noneOption={NO_ROUTER_OPTION}
                errorMessage={errors.routerHostID ? errors.routerHostID.message : undefined}
              />

              {routerHostID === NO_HOST_FLAG && (
                <HintText className="mt-8">
                  <AlertIcon /> This route will not be reachable without a router
                </HintText>
              )}
            </div>
          </FieldSet>
          <FieldSet legend="Routable CIDRs">
            <Field>
              <Label className="visually-hidden">Routable CIDRs</Label>
              {errors.routableCIDRs && <ErrorMessage>{errors.routableCIDRs.message}</ErrorMessage>}
              <RoutableCIDRs
                cidrs={cidrs}
                onRemove={removeCIDR}
                onEdit={(index) => {
                  setEditCIDRIndex(index);
                }}
              >
                <AddButton
                  onClick={() => {
                    setShowAddCIDRModal(true);
                  }}
                >
                  Add CIDR
                </AddButton>
              </RoutableCIDRs>
            </Field>
          </FieldSet>
          <FieldSet legend="Subscription">
            <ComboboxTags
              name="tags"
              label="Tags"
              control={tagsControl}
              hasError={Boolean(tagsFormState.errors.tags)}
              helpText="Hosts, lighthouses, and relays assigned these tags will subscribe to this route"
            />
          </FieldSet>
          <FieldSet legend="Firewall rules">
            <CIDRFirewallRulesList
              className={styles.RouteForm_firewallTable}
              firewallRules={firewallRuleFields}
              hideOverflowX={true}
              onRemove={removeRule}
              onEdit={setEditingRuleIndex}
              onDuplicate={(index) => {
                setAddRuleModal({
                  defaultValues: assert(firewallRuleFields[index], 'expected duplicated rule to exist'),
                });
              }}
            >
              <AddButton
                onClick={() => {
                  setAddRuleModal({});
                }}
              >
                Add firewall rule
              </AddButton>
            </CIDRFirewallRulesList>
          </FieldSet>
        </Spacer>
        {showAddCIDRModal ?
          <AddCIDRModal
            onCIDRAdded={(cidrConfig) => {
              appendCIDR(cidrConfig);
              setShowAddCIDRModal(false);
            }}
            onCancel={() => {
              setShowAddCIDRModal(false);
            }}
          />
        : null}
        {valueBeingEdited ?
          <EditCIDRModal
            cidr={valueBeingEdited}
            onCancel={() => {
              setEditCIDRIndex(null);
            }}
            onSave={(cidr) => {
              updateCIDR(editCIDRIndex, cidr);
              setEditCIDRIndex(null);
            }}
          />
        : null}
        <ConfirmChangesBanner
          isOpen={isDirty}
          title={isSubmitted && !isValid ? 'Unable to save.  Please update and try again.' : undefined}
          primaryButton={<Button type="submit">Save</Button>}
          secondaryButton={<CancelLinkButton fallbackTo="/routes" />}
        />
        {editingRuleIndex != null ?
          <EditCIDRFirewallRuleModal
            firewallRule={assert(
              firewallRuleFields[editingRuleIndex],
              'expected editingRuleIndex to find firewall rules'
            )}
            onSave={(values) => {
              updateRule(editingRuleIndex, values);
              setEditingRuleIndex(null);
            }}
            onClose={() => {
              setEditingRuleIndex(null);
            }}
          />
        : null}
        {addRuleModal && (
          <AddCIDRFirewallRuleModal
            defaultValues={addRuleModal.defaultValues}
            onSave={(values) => {
              appendRule(values);
              setAddRuleModal(null);
            }}
            onClose={() => {
              setAddRuleModal(null);
            }}
          />
        )}
      </form>
    </>
  );
}
